/**
 * Performance utilities for PAYR SDK
 */

/**
 * Debounce: delay rapid function calls. Only the last call within the delay executes.
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delayMs: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (this: unknown, ...args: Parameters<T>) {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn.apply(this, args);
    }, delayMs);
  };
}

/**
 * Throttle: limit function call frequency. At most one call per interval.
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  intervalMs: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let latestThis: unknown = undefined;
  let latestArgs: Parameters<T> = [] as unknown as Parameters<T>;

  return function (this: unknown, ...args: Parameters<T>) {
    // Capture for deferred invoke; no-this-alias disabled: throttle must use latest call context
    latestThis = this; // eslint-disable-line @typescript-eslint/no-this-alias
    latestArgs = args;

    const now = Date.now();
    const remaining = intervalMs - (now - lastCall);

    const invoke = () => {
      lastCall = Date.now();
      timeoutId = null;
      fn.apply(latestThis, latestArgs);
    };

    if (remaining <= 0) {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      invoke();
    } else if (timeoutId == null) {
      timeoutId = setTimeout(invoke, remaining);
    }
  };
}
